import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Users, Calendar, Settings, LogOut, Plus, CheckCircle, Clock, User, Home, TrendingUp, Star, Eye, MapPin } from 'lucide-react';
import { goHome } from '../utils/navigation';
import AdminUserManagement from './AdminUserManagement';
import AdminEventManagement from './AdminEventManagement';
import CommunityUserManagement from './CommunityUserManagement';
import { DASHBOARD_CONFIG } from '../config/dashboard';
import {apiService} from '../services/api';

type DashboardSection = 'overview' | 'users' | 'events' | 'communityUsers';

const AdminDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeSection, setActiveSection] = useState<DashboardSection>('overview');

  // Check if user has admin dashboard access
  const hasAccess = user && (user.role === 'ADMIN' || user.role === 'COMMITTEE_ADMIN' || user.role === 'VENDOR_ADMIN');
  const isAdmin = user?.role === 'ADMIN';
  const isCommunityAdmin = user && (user.role === 'COMMITTEE_ADMIN' || user.role === 'VENDOR_ADMIN');

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="font-runtime text-2xl font-bold text-black mb-2">
            Access Denied
          </h2>
          <p className="font-lucy-rose text-gray-600">
            This dashboard is only for administrators.
          </p>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    goHome();
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'users':
        // Only render user management for admin users
        if (isAdmin) {
          return <AdminUserManagement />;
        } else {
          setActiveSection('overview');
          return <DashboardOverview setActiveSection={setActiveSection} isAdmin={isAdmin} />;
        }
      case 'events':
        return <AdminEventManagement showHeader={true} />;
      case 'communityUsers':
        if (isCommunityAdmin) {
          return <CommunityUserManagement />;
        } else {
          setActiveSection('overview');
          return <DashboardOverview setActiveSection={setActiveSection} isAdmin={isAdmin} />;
        }
      default:
        return <DashboardOverview setActiveSection={setActiveSection} isAdmin={isAdmin} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="p-2 bg-gradient-to-br from-dpn-pink to-dpn-raspberry rounded-lg shadow-lg mr-4">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {isAdmin ? 'Admin Dashboard' : 'Committee Dashboard'}
                </h1>
                <p className="text-sm text-gray-600">
                  Welcome back, <span className="font-medium text-dpn-pink">{user?.role}</span>!
                </p>
              </div>
            </div>
            {/* <div className="flex items-center space-x-4">
              <button
                onClick={() => goHome()}
                className="flex items-center space-x-2 text-dpn-olive hover:text-dpn-pink transition-colors duration-200"
              >
                <Home className="h-5 w-5" />
                <span>Home</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-dpn-olive hover:text-dpn-pink transition-colors duration-200"
              >
                <LogOut className="h-5 w-5" />
                <span>Logout</span>
              </button>
            </div> */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveSection('overview')}
              className={`px-6 py-4 text-sm font-medium transition-colors duration-200 ${
                activeSection === 'overview'
                  ? 'text-dpn-pink border-b-2 border-dpn-pink'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveSection('events')}
              className={`px-6 py-4 text-sm font-medium transition-colors duration-200 ${
                activeSection === 'events'
                  ? 'text-dpn-pink border-b-2 border-dpn-pink'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Events
            </button>
            {isAdmin && (
              <button
                onClick={() => setActiveSection('users')}
                className={`px-6 py-4 text-sm font-medium transition-colors duration-200 ${
                  activeSection === 'users'
                    ? 'text-dpn-pink border-b-2 border-dpn-pink'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Users
              </button>
            )}
            {isCommunityAdmin && (
              <button
                onClick={() => setActiveSection('communityUsers')}
                className={`px-6 py-4 text-sm font-medium transition-colors duration-200 ${
                  activeSection === 'communityUsers'
                    ? 'text-dpn-pink border-b-2 border-dpn-pink'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Committee Users
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        {renderSection()}
      </div>
    </div>
  );
};

interface DashboardOverviewProps {
  setActiveSection: (section: DashboardSection) => void;
  isAdmin: boolean;
}

// Trending Events Section Component
const TrendingEventsSection: React.FC<{ loading: boolean }> = ({ loading }) => {
  const { token } = useAuth();
  const [trendingEvents, setTrendingEvents] = useState<any[]>([]);
  const [trendingLoading, setTrendingLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTrendingEvents = async () => {
      if (!token) return;
      
      setTrendingLoading(true);
      setError(null);
      try {
        const response = await apiService.getTrendingEvents({ limit: 6 }); // Get top 6 trending events
        if (response.success && response.data) {
          setTrendingEvents(response.data);
        } else {
          throw new Error('Failed to fetch trending events');
        }
      } catch (error) {
        console.error('Error fetching trending events:', error);
        setError('Failed to load trending events');
      } finally {
        setTrendingLoading(false);
      }
    };

    fetchTrendingEvents();
  }, [token]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-red-50 to-orange-50 rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-lg">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Trending Events</h3>
          </div>
          <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
            🔥 Hot
          </span>
        </div>
      </div>
      <div className="p-6">
        {trendingLoading || loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 h-40 rounded-lg mb-3"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center text-red-500 py-8">
            <TrendingUp className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <p className="text-lg font-medium mb-2">Unable to Load Trending Events</p>
            <p className="text-sm text-gray-500">{error}</p>
          </div>
        ) : trendingEvents.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-700 mb-2">No Trending Events</p>
            <p className="text-sm text-gray-500">Check back later for popular events</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {trendingEvents.map((event: any, index: number) => (
              <div key={event.id || index} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 border border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                      #{index + 1}
                    </span>
                    {event.isVerified && (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                  <TrendingUp className="h-4 w-4 text-red-500" />
                </div>
                
                <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                  {event.title || 'Untitled Event'}
                </h4>
                
                <div className="space-y-2 text-sm text-gray-600">
                  {event.location && (
                    <div className="flex items-center space-x-1">
                      <MapPin className="h-3 w-3" />
                      <span className="truncate">{event.location}</span>
                    </div>
                  )}
                  
                  {event.expectedAttendance && (
                    <div className="flex items-center space-x-1">
                      <Eye className="h-3 w-3" />
                      <span>{event.expectedAttendance.toLocaleString()} expected</span>
                    </div>
                  )}
                  
                  {event.startDate && (
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-3 w-3" />
                      <span>{new Date(event.startDate).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
                
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {event.createdByRole || 'Committee'}
                    </span>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-xs text-red-600 font-medium">Trending</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const DashboardOverview: React.FC<DashboardOverviewProps> = ({ setActiveSection, isAdmin }) => {
  const { token } = useAuth();
  interface DashboardStats {
  totalEvents: number;
  pendingEvents: number;
  verifiedEvents: number;
  featuredEvents: number;
  trendingEvents: number;
  totalUsers: number;
  committeeUsers: number;
  adminUsers: number;
  devoteeUsers: number;
  vendorUsers: number;
}

const [stats, setStats] = useState<DashboardStats>({
  totalEvents: DASHBOARD_CONFIG.DEFAULT_STATS.TOTAL_EVENTS,
  pendingEvents: DASHBOARD_CONFIG.DEFAULT_STATS.PENDING_APPROVALS,
  verifiedEvents: 0,
  featuredEvents: 0,
  trendingEvents: 0,
  totalUsers: DASHBOARD_CONFIG.DEFAULT_STATS.TOTAL_USERS,
  committeeUsers: 0,
  adminUsers: 0,
  devoteeUsers: 0,
  vendorUsers: 0
});
const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
  const fetchDashboardStats = async () => {
    if (!token) {
      setError('Authentication token not available');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getDashboardStats(token);

      if (response.success && response.data) {
        setStats(response.data);
      } else {
        throw new Error('Failed to fetch dashboard statistics');
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  fetchDashboardStats();
}, [token]);

if (error) {
  return (
    <div className="bg-white rounded-lg shadow p-6 text-center">
      <div className="text-red-500 mb-4">⚠️</div>
      <p className="text-gray-600 mb-4">{error}</p>
      <button
        onClick={() => window.location.reload()}
        className="bg-dpn-pink text-white px-4 py-2 rounded-lg hover:bg-dpn-pink-dark transition-colors"
      >
        Retry
      </button>
    </div>
  );
}

  return (
    <div className="space-y-8">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-8">
        {/* Total Users Card */}
        <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border border-gray-100">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-600 mb-1">Total Users</p>
              {loading ? (
                <div className="animate-pulse bg-gray-200 h-8 w-20 rounded"></div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">{stats.totalUsers.toLocaleString()}</p>
              )}
            </div>
          </div>
        </div>

        {/* Total Events Card */}
        <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border border-gray-100">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg">
              <Calendar className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-600 mb-1">Total Events</p>
              {loading ? (
                <div className="animate-pulse bg-gray-200 h-8 w-20 rounded"></div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">{stats.totalEvents.toLocaleString()}</p>
              )}
            </div>
          </div>
        </div>

        {/* Pending Events Card */}
        <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border border-gray-100">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg">
              <Clock className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-600 mb-1">Pending Events</p>
              {loading ? (
                <div className="animate-pulse bg-gray-200 h-8 w-20 rounded"></div>
              ) : (
                <div className="flex items-center">
                  <p className="text-2xl font-bold text-gray-900">{stats.pendingEvents.toLocaleString()}</p>
                  {stats.pendingEvents > 0 && (
                    <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
                      Action Required
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Verified Events Card */}
        <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border border-gray-100">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg">
              <CheckCircle className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-600 mb-1">Verified Events</p>
              {loading ? (
                <div className="animate-pulse bg-gray-200 h-8 w-20 rounded"></div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">{stats.verifiedEvents.toLocaleString()}</p>
              )}
            </div>
          </div>
        </div>

        {/* Featured Events Card */}
        <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border border-gray-100">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg shadow-lg">
              <Star className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-600 mb-1">Featured Events</p>
              {loading ? (
                <div className="animate-pulse bg-gray-200 h-8 w-20 rounded"></div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">{stats.featuredEvents.toLocaleString()}</p>
              )}
            </div>
          </div>
        </div>

        {/* Trending Events Card */}
        <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border border-gray-100">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-lg">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-600 mb-1">Trending Events</p>
              {loading ? (
                <div className="animate-pulse bg-gray-200 h-8 w-20 rounded"></div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">{stats.trendingEvents.toLocaleString()}</p>
              )}
            </div>
          </div>
        </div>

        {/* Committee Users Card */}
        <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border border-gray-100">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg shadow-lg">
              <User className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-600 mb-1">Committee Users</p>
              {loading ? (
                <div className="animate-pulse bg-gray-200 h-8 w-20 rounded"></div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">{stats.committeeUsers.toLocaleString()}</p>
              )}
            </div>
          </div>
        </div>

        {/* Admin Users Card */}
        <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border border-gray-100">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-dpn-pink to-dpn-raspberry rounded-lg shadow-lg">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-600 mb-1">Admin Users</p>
              {loading ? (
                <div className="animate-pulse bg-gray-200 h-8 w-20 rounded"></div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">{stats.adminUsers.toLocaleString()}</p>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* Page Title */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard Overview</h1>
        <p className="text-gray-600">Welcome back! Here's what's happening with your Durga Puja Network.</p>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Add New User - Only for Admin */}
          {isAdmin && (
            <button
              onClick={() => setActiveSection('users')}
              className="bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border border-blue-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-6 text-left group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="p-2 bg-blue-500 rounded-lg mb-3 w-fit">
                    <User className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Add New User</h3>
                  <p className="text-gray-600 text-sm">Create user accounts and manage permissions</p>
                </div>
                <div className="text-blue-600 group-hover:translate-x-1 transition-transform duration-200 text-xl">
                  →
                </div>
              </div>
            </button>
          )}

          {/* Create New Event */}
          <button
            onClick={() => setActiveSection('events')}
            className="bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border border-green-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-6 text-left group"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="p-2 bg-green-500 rounded-lg mb-3 w-fit">
                  <Plus className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Event</h3>
                <p className="text-gray-600 text-sm">Add new Durga Puja events to the network</p>
              </div>
              <div className="text-green-600 group-hover:translate-x-1 transition-transform duration-200 text-xl">
                →
              </div>
            </div>
          </button>

          {/* Manage Events */}
          <button
            onClick={() => setActiveSection('events')}
            className="bg-gradient-to-br from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 border border-purple-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-6 text-left group"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="p-2 bg-purple-500 rounded-lg mb-3 w-fit">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Manage Events</h3>
                <p className="text-gray-600 text-sm">Review, edit and approve events</p>
              </div>
              <div className="text-purple-600 group-hover:translate-x-1 transition-transform duration-200 text-xl">
                →
              </div>
            </div>
          </button>
        </div>
      </div>
      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-xl">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
            <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">Live</span>
          </div>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center space-x-3 animate-pulse">
                  <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <div className="p-4 bg-gray-50 rounded-full w-fit mx-auto mb-4">
                <Calendar className="h-12 w-12 text-gray-400" />
              </div>
              <p className="text-lg font-medium text-gray-700 mb-2">No recent activity</p>
              <p className="text-sm text-gray-500">Recent dashboard activities and system events will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};



export default AdminDashboard; 